import {
  AUTH_RIGHT_PACKAGE_CODES,
  TEM_RIGHT_PACKAGE_CODES,
  UPGRADE_RIGHT_PACKAGE_CODES,
  UPGRADE_RIGHT_PLUS_PACKAGE_CODES,
} from "@/constants/vip";
import formatTime from "@/utils/formatTime";
import dayjs from "dayjs";
import { at, curry, last, round, trimEnd } from "lodash";

const basicJudge = curry((rightPackagesCodes, sku) => {
  if (!sku.right_packages) return false;
  const identifications = sku.right_packages.map((p) => p.identification);
  for (let i = 0; i < identifications.length; i++) {
    const iden = identifications[i];
    if (rightPackagesCodes.includes(iden)) {
      return true;
    }
  }
});

/**
 * 是否是24H加油包
 * @param {*} sku
 */
export const isTemPkg = basicJudge(TEM_RIGHT_PACKAGE_CODES);
/**
 * 是否是升级次数包
 * @param {*} sku
 */
export const isUpgradePkg = basicJudge(UPGRADE_RIGHT_PACKAGE_CODES);

/**
 * 是否是升级次数包
 * @param {*} sku
 */
export const isUpgradePlusPkg = basicJudge(UPGRADE_RIGHT_PLUS_PACKAGE_CODES);
/**
 * 是否是扩展授权包
 * @param {*} sku
 * @returns
 */
export const isAuthPkg = basicJudge(AUTH_RIGHT_PACKAGE_CODES);

export function getRightPkgCode(good) {
  return at(good, "skus[0].right_packages[0].identification")[0];
}

export function getSkuRightPkgCode(sku) {
  return at(sku, "right_packages[0].identification")[0];
}

/**
 * 添加商品时，将商品转化成支结算明细项目
 * @param {*} good 商品信息
 * @param {*} number 商品数量
 * @param {{
 *  start,
 *  end,
 *  extend,
 * }} option 扩展 start: 开始计算生效的起始时间
 * @returns
 */
export function getPayDetailByGood(
  good,
  number = 1,
  { start, end, isUpgrade, extend = {}, skuCode } = {}
) {
  const start_at = start ? new Date(start) : new Date();
  const sku = skuCode
    ? good.skus.find((sku) => sku.custom_code === skuCode)
    : good.skus[0];
  const upgradeYear = end
    ? dayjs(end).diff(dayjs(new Date()), "y", true).toFixed(2)
    : 0;

  const detail = {
    code: good.code,
    price: isUpgrade
      ? round(
          (sku.price / 365) *
            dayjs(end).diff(dayjs(Date.now()), "days", true).toFixed(0),
          2
        )
      : sku.price,
    name: sku.name,
    skuId: sku.id,
    number: number,
    good: good,
    startTime: start_at,
    years: isUpgrade && upgradeYear > 1 && upgradeYear,
    expire: end || getExpireTime(start_at, sku, number),
    expireTime: end
      ? formatTime(new Date(end), "YYYY/MM/DD")
      : formatTime(getExpireTime(start_at, sku, number), "YYYY/MM/DD"),
    ...extend,
  };

  // 24小时加油包只从现在开始
  if (isTemPkg(sku)) {
    detail.startTime = new Date();
    detail.expireTime = formatTime(
      new Date().getTime() + number * 24 * 3600 * 1000,
      "YYYY/MM/DD HH:mm:ss"
    );
  }

  return detail;
}

/**
 * 获取权益列表中最晚的到期时间
 * @param {{
 * end_at: string|number|Date
 * }} list
 */
export function getLastExpireTime(list) {
  if (list.length) {
    return Math.max(...list.map(({ end_at }) => new Date(end_at)));
  }
  return new Date();
}

/**
 * 获取可购买最大数量的商品
 * 根据会员时间、已买商品最晚过期时间、商品周期来计算最大可购买个数
 * @param {*} hasRightList 已购买的权益列表
 * @param {{expires_at:string}} vip 对应已有会员信息
 * @param {*} sku
 */
export function getMaxBuyNumber(hasRightList, vip, sku) {
  const lastExpireDate = new Date(getLastExpireTime(hasRightList));
  const vipExpireDate = new Date(vip.expires_at);

  const { unit, num, err } = parseSkuDuration(sku);

  if (err) return err;

  const diff = dayjs(vipExpireDate).diff(dayjs(lastExpireDate), unit, true);

  return Math.floor(diff / num);
}

/**
 * 解析商品周期，方便计算
 * @param {*} sku
 * @returns
 */
export function parseSkuDuration(sku) {
  // 不存在商品周期则返回0
  const skuDuration = sku?.props?.duration?.value;
  // return 0
  if (!skuDuration)
    return {
      err: "不存在商品周期",
    };

  const unit = last(skuDuration);
  const num = trimEnd(skuDuration, unit);

  return {
    unit,
    num,
  };
}
/**
 * 解析商品周期单位，方便显示， 1年显示：年，3个月显示：季， 1个月显示月， 其他则显示数字加单位
 * @param {*} sku
 * @returns
 */
export function getSkuUnit(sku) {
  const parseRes = parseSkuDuration(sku);
  const { unit, num } = parseRes;

  if (unit === "M" && num == 3) return "季";
  if (unit === "M" && num == 12) return "年";
  if (unit === "M") return (num == 1 ? "" : num) + "月";
  if (unit === "D") return (num == 1 ? "" : num) + "天";
  if (unit === "M") return (num == 1 ? "" : num) + "天";
  if (unit === "Y") return (num == 1 ? "" : num) + "年";
}

export function getExpireTime(start_at, sku, quantity) {
  const { unit, num, err } = parseSkuDuration(sku);
  if (err) return err;

  return dayjs(start_at)
    .add(num * quantity, unit)
    .toDate();
}
/**
 * 获取升级会员时，会员有效时间
 * @param {*} vips 当前生效会员信息
 * @param {*} sku 商品信息
 * @returns
 */
export function getVipUpgradeExpireTime(vips, sku) {
  const vipExpires = vips[0].expires_at;

  const { unit, num } = parseSkuDuration(sku);
  // 现在时间添加一个商品周期，与会员过期时间比较，如果
  if (dayjs().add(num, unit).toDate() > dayjs(vipExpires)) {
    // 会员剩余时长不足一个商品周期，将补足一整个商品周期
    return dayjs().add(num, unit).toDate();
  } else {
    // 会员剩余时长大于一个商品周期，过期时间与会员过期时间一致
    return new Date(vipExpires);
  }
}

/**
 * 获取购买会员扩展包（升级包、扩展授权包）时的过期时间
 * @param {*} vips 当前生效会员信息
 * @param {*} sku 商品信息
 * @param {*} list 已购买在生效范围内的权益包列表
 * @param {*} number 购买数量
 */
export function getVipPkgExpireTime(sku, list, number) {
  const expire = getExpireTime(getLastExpireTime(list), sku, number);
  return expire;
}

/**
 * 获取购买会员扩展包（24小时加油包）时的过期时间
 * @param {*} sku 商品信息
 */
export function getVipTemExpireTime(sku) {
  const { unit, num, err } = parseSkuDuration(sku);
  if (err) return err;
  return dayjs().add(num, unit).toDate();
}
/**
 * 获取购买/续费会员后的有效时间
 * @param {*} vips
 * @param {*} sku
 * @returns
 */
export function getVipExpireTime(vips, sku) {
  const start_at = vips && vips.length ? vips[0].expires_at : new Date();
  const { unit, num, err } = parseSkuDuration(sku);
  if (err) return err;
  return dayjs(start_at).add(num, unit).toDate();
}

export function isExpVip(user) {
  return user?.extra.material_vip === 5;
}

/**
 * 解析code，返回skus集合
 * @param {[]} goods
 * @param {string} codes 多个用逗号分隔，若要指明sku，请按此格式填写： '商品code.skuCode'
 * @returns {[]}
 */
export function getSkusByCode(goods, codes) {
  const codeList = codes.split(",");
  return codeList
    .map((code) => {
      const goodCode = code.split(".")[0];
      const skuCode = code.split(".")[1];
      const findGood = goods.find((g) => g.code === goodCode);
      let sku;
      if (skuCode) {
        sku = findGood?.skus.find((sku) => sku.custom_code === skuCode);
      } else {
        sku = goods.find((g) => g.code === goodCode)?.skus[0];
      }

      if (sku) {
        return { ...sku, good: findGood };
      } else {
        console.warn(`未找到code：${code} 的商品，请检查配置`);
      }
    })
    .filter((sku) => sku);
}

export function getPrice(skus, activity) {
  const activitySkus = activity ? activity.detail.subscribe_goods : [];
  return skus.reduce((pre, cur) => {
    const activityPrice = activitySkus.find(
      (sku) => sku.sku_id == cur.id
    )?.price;

    return pre + (activityPrice === undefined ? cur.price : activityPrice);
  }, 0);
}

export function getOrigrinPrice(skus, activity, suffix = 0, base = 0) {
  const nowPrice = getPrice(skus, activity);
  const originalPrice = skus.reduce((pre, cur) => pre + cur?.original_price, 0);

  return nowPrice < originalPrice ? suffix + (originalPrice + base) : null;
}

/**
 * 添加商品时，将商品转化成支结算明细项目
 * @param {*} skus 商品信息
 * @param {*} number 商品数量
 * @param {{
 *  start,
 *  end,
 *  extend,
 * }} option 扩展 start: 开始计算生效的起始时间
 * @returns
 */
export function getPayDetailBySkus(
  skus,
  number = 1,
  { start, end, isUpgrade, extend = {}, rightPkgCodeMap = {} } = {}
) {
  const upgradeYear = end
    ? dayjs(end).diff(dayjs(new Date()), "y", true).toFixed(2)
    : 0;
  return skus.map((sku) => {
    let start_at = start ? new Date(start) : new Date();
    const good = sku.good;
    const rightPkgCode = getSkuRightPkgCode(sku);

    if (good.type !== "vip") {
      start_at = new Date(rightPkgCodeMap[rightPkgCode]?.end_at || Date.now());
    }
    const detail = {
      code: good.code,
      // 升级场景下进行价格折算，总价折算成天的价格，然后与实际购买天数相乘
      price: isUpgrade
        ? round(
            (sku.price / 365) *
              dayjs(end).diff(dayjs(Date.now()), "days", true).toFixed(0),
            2
          )
        : sku.price,
      // 明细显示商品名称
      name: sku.name,
      // 商品id，支付所需参数
      skuId: sku.id,
      // 购买数量，支付所需参数
      number: number,
      // 对应的商品
      good: good,
      // 开始生效时间，明细进行展示
      startTime: start_at,
      // 升级场景限制折算的年份
      years: isUpgrade && upgradeYear > 1 && upgradeYear,
      // 过期时间
      expire: end || getExpireTime(start_at, sku, number),
      // 过期日期，明细进行展示
      expireTime: end
        ? formatTime(new Date(end), "YYYY/MM/DD")
        : formatTime(getExpireTime(start_at, sku, number), "YYYY/MM/DD"),
      ...extend,
    };

    // 24小时加油包只从现在开始
    if (isTemPkg(sku)) {
      detail.startTime = new Date();
      detail.expireTime = formatTime(
        new Date().getTime() + number * 24 * 3600 * 1000,
        "YYYY/MM/DD HH:mm:ss"
      );
    }

    return detail;
  });
}
